import { useState, useEffect, useRef, useCallback } from 'react';
import { getEmployeeDetailsByLogin, getJobProfileClassification } from "src/Ateam-BI/Templates/Utils/AppActivityUtils";
import { AI_AGENT_DEBUG_LOGS } from "src/Ateam-BI/AutoMate/constants";

// Persona classification constants
export const PERSONA = Object.freeze({
  TECH: 'TECH',
  NON_TECH: 'NON_TECH',
  UNKNOWN: 'UNKNOWN',
});

// Configurable default — change to PERSONA.TECH if the team decides to flip the default
export const DEFAULT_PERSONA_CLASSIFICATION = PERSONA.NON_TECH;

// Technical Classification group name in the Reference API response
const TECH_CLASSIFICATION_GROUP = 'Technical Classification';

/**
 * Async classification using PAPI Reference API.
 * 1. Takes jobProfileId from the employee API response
 * 2. Calls /v2/reference/jobprofiles to get jobClassification
 * 3. Looks for group: "Technical Classification"
 * 4. Returns 'TECH' if found with tech value, 'NON_TECH' otherwise
 * 5. Returns DEFAULT_PERSONA_CLASSIFICATION on any failure
 */
export const classifyPersonaAsync = async (jobProfileId) => {
  if (!jobProfileId) return DEFAULT_PERSONA_CLASSIFICATION;

  try {
    const response = await getJobProfileClassification(jobProfileId);
    const classifications = response?.jobClassifications || response?.jobClassification || [];

    const techEntry = classifications.find(
      (c) => c.group === TECH_CLASSIFICATION_GROUP
    );

    if (!techEntry) return PERSONA.NON_TECH;

    // Positive matching: only classify as TECH when the value explicitly
    // contains 'Technical' AND does not contain 'Non-Technical'. This avoids
    // misclassifying unexpected values like "Unclassified", "Pending", "N/A",
    // or empty strings as TECH (AutoSDE recommendation).
    if (
      techEntry.jobCodeClassification &&
      techEntry.jobCodeClassification.includes('Technical') &&
      !techEntry.jobCodeClassification.includes('Non-Technical')
    ) {
      return PERSONA.TECH;
    }

    if (
      techEntry.value &&
      techEntry.value.includes('Technical') &&
      !techEntry.value.includes('Non-Technical')
    ) {
      return PERSONA.TECH;
    }

    return PERSONA.NON_TECH;
  } catch (error) {
    console.error('Failed to fetch job profile classification:', {
      jobProfileId,
      error: error?.message || error?.errors?.[0]?.message || String(error),
      name: error?.name || 'unknown',
      raw: error,
    });
    return DEFAULT_PERSONA_CLASSIFICATION;
  }
};

// TECH job family codes (O(1) Set lookup)
const TECH_JOB_FAMILY_SET = new Set(['DATENG', 'SFTDE', 'SDE']);

// TECH title patterns (word-boundary, uppercased input)
const TECH_TITLE_PATTERNS = [
  /\bDATA ENGINEER\b/,
  /\bSOFTWARE DEVELOPMENT ENGINEER\b/,
  /\bSOFTWARE ENGINEER\b/,
  /\bBUSINESS INTELLIGENCE ENGINEER\b/,
];

/**
 * Pure function — no I/O, no side effects, no mutation.
 * Priority: jobFamilyName match → jobProfileName pattern → NON_TECH → UNKNOWN
 */
export const classifyPersona = (papiJobData) => {
  if (!papiJobData || typeof papiJobData !== 'object') return PERSONA.UNKNOWN;

  const jf = (papiJobData.jobFamilyName || '').toUpperCase().trim();
  const jp = (papiJobData.jobProfileName || '').toUpperCase().trim();

  // Both fields absent → UNKNOWN
  if (!jf && !jp) return PERSONA.UNKNOWN;

  // 1. jobFamilyName exact match takes priority
  if (jf && TECH_JOB_FAMILY_SET.has(jf)) return PERSONA.TECH;

  // 2. jobProfileName pattern match (runs even when jobFamilyName is missing)
  if (jp && TECH_TITLE_PATTERNS.some((p) => p.test(jp))) return PERSONA.TECH;

  // 3. At least one field present but no TECH match → NON_TECH
  return PERSONA.NON_TECH;
};

// Job family codes are exact short codes — use a direct map for O(1) lookup
const JOB_FAMILY_TO_LABEL = {
  'DATENG': 'Data Engineer',
  'PROSRV': 'Professional Services II',
  'ACCTG': 'Accountant',
  'FINAC': 'Accountant',
  'SFTDE': 'Software Engineer',
  'SDE': 'Software Engineer',
};

// Title keywords use word-boundary matching to avoid false positives
// e.g. 'LEAD SOFTWARE ENGINEER' should match 'SOFTWARE ENGINEER' but not 'SOFTWARE SALES MANAGER'
const TITLE_PATTERNS = [
  { pattern: /\bDATA ENGINEER\b/, label: 'Data Engineer' },
  { pattern: /\bPROFESSIONAL SERVICES\b/, label: 'Professional Services II' },
  { pattern: /\bACCOUNTANT\b/, label: 'Accountant' },
  { pattern: /\bSOFTWARE DEVELOPMENT ENGINEER\b|\bSOFTWARE ENGINEER\b/, label: 'Software Engineer' },
];

export const resolvePersona = (jobFamily, title) => {
  const jf = (jobFamily || '').toUpperCase().trim();
  const t = (title || '').toUpperCase();

  // Job family exact match takes priority
  if (jf && JOB_FAMILY_TO_LABEL[jf]) return JOB_FAMILY_TO_LABEL[jf];

  // Fall back to word-boundary title matching
  const titleMatch = TITLE_PATTERNS.find(({ pattern }) => pattern.test(t));
  return titleMatch ? titleMatch.label : 'General';
};

// Valid override modes for setOverrideMode validation
const VALID_OVERRIDE_MODES = ['Auto', PERSONA.TECH, PERSONA.NON_TECH];

export const usePersona = (papiAlias, firstName, enabled = true) => {
  const [personaText, setPersonaText] = useState(
    (enabled && papiAlias) ? 'Loading persona...' : 'For persona based conversations, change the source in the settings.'
  );
  const [userName, setUserName] = useState(firstName);
  const [hasApiName, setHasApiName] = useState(false);
  const firstNameRef = useRef(firstName);
  firstNameRef.current = firstName;

  // Track whether telemetry has been emitted for the current alias
  const telemetryEmittedRef = useRef(null);

  // New state: raw classification from classifyPersonaAsync
  const [personaClassification, setPersonaClassification] = useState(PERSONA.UNKNOWN);

  // New state: PAPI job data for payload construction
  const [papiJobData, setPapiJobData] = useState(null);

  // New state: override mode initialized from localStorage
  const [overrideMode, setOverrideModeState] = useState(() => {
    if (!papiAlias) return 'Auto';
    try {
      return localStorage.getItem(`${papiAlias}_personaOverride`) || 'Auto';
    } catch {
      // localStorage unavailable — default to Auto
      return 'Auto';
    }
  });

  // Validate and persist override mode to localStorage
  const setOverrideMode = useCallback((mode) => {
    if (!VALID_OVERRIDE_MODES.includes(mode)) return;
    try {
      if (papiAlias) {
        localStorage.setItem(`${papiAlias}_personaOverride`, mode);
      }
    } catch {
      // localStorage unavailable — still update in-memory state
    }
    setOverrideModeState(mode);
  }, [papiAlias]);

  // Derive effectivePersona: override takes precedence, then classification with UNKNOWN → DEFAULT
  const effectivePersona =
    overrideMode !== 'Auto'
      ? overrideMode
      : personaClassification === PERSONA.UNKNOWN
        ? DEFAULT_PERSONA_CLASSIFICATION
        : personaClassification;

  // Emit persona_resolved telemetry once per alias per session (gated behind AI_AGENT_DEBUG_LOGS)
  const emitPersonaTelemetry = (alias, classification, papiLookupSucceeded) => {
    try {
      if (!AI_AGENT_DEBUG_LOGS) return;
      if (telemetryEmittedRef.current === alias) return;
      telemetryEmittedRef.current = alias;

      // Compute effectivePersona at the time of emission
      const currentOverride = overrideMode;
      const resolvedEffectivePersona =
        currentOverride !== 'Auto'
          ? currentOverride
          : classification === PERSONA.UNKNOWN
            ? DEFAULT_PERSONA_CLASSIFICATION
            : classification;

      console.log('[TELEMETRY] persona_resolved', {
        alias,
        personaClassification: classification,
        effectivePersona: resolvedEffectivePersona,
        overrideMode: currentOverride,
        papiLookupSucceeded,
      });
    } catch {
      // Telemetry emission failure is silent — never affects persona resolution
    }
  };

  // Re-initialize overrideMode from localStorage when alias changes
  useEffect(() => {
    if (!papiAlias) {
      setOverrideModeState('Auto');
      return;
    }
    try {
      const stored = localStorage.getItem(`${papiAlias}_personaOverride`);
      setOverrideModeState(stored || 'Auto');
    } catch {
      setOverrideModeState('Auto');
    }
  }, [papiAlias]);

  // Keep userName in sync with firstName when no API-derived name is available
  useEffect(() => {
    if (!hasApiName) {
      setUserName(firstName);
    }
  }, [firstName, hasApiName]);

  useEffect(() => {
    if (!enabled || !papiAlias) {
      setHasApiName(false);
      setUserName(firstNameRef.current);
      setPersonaText('For persona based conversations, change the source in the settings.');
      setPersonaClassification(PERSONA.UNKNOWN);
      setPapiJobData(null);
      telemetryEmittedRef.current = null;
      return;
    }
    setPersonaText('Loading persona...');
    setHasApiName(false);
    setUserName(firstNameRef.current);
    setPersonaClassification(PERSONA.UNKNOWN);
    setPapiJobData(null);
    let cancelled = false;
    const fetchPersona = async () => {
      try {
        const result = await getEmployeeDetailsByLogin(papiAlias);
        if (cancelled) return;
        const raw = result?.data?.getEmployeeDetailsByLogin;
        if (!raw) throw new Error('Empty employee details response');
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;

        // Extract and set userName (existing behavior)
        const apiFirstName = data?.basicInfo?.firstName;
        if (apiFirstName) {
          setHasApiName(true);
          setUserName(apiFirstName);
        } else {
          setHasApiName(false);
          setUserName(firstNameRef.current);
        }

        // Extract job data fields for personaText (existing behavior)
        const jf = data?.job?.jobFamilyName || data?.jobFamilyName || '';
        const title = data?.basicInfo?.businessTitle || data?.job?.businessTitle || '';
        setPersonaText(`Selected Persona is - ${resolvePersona(jf, title)}`);

        // Extract papiJobData for payload construction
        const jobFamilyName = data?.job?.jobFamilyName || data?.jobFamilyName || '';
        const jobProfileName = data?.basicInfo?.jobProfileName || data?.job?.jobProfileName || data?.jobProfileName || '';
        const jobLevel = data?.job?.jobLevel || data?.jobLevel || '';
        const jobProfileId = data?.basicInfo?.jobProfileId || data?.job?.jobProfileId || data?.jobProfileId || '';
        setPapiJobData({ jobFamilyName, jobProfileName, jobLevel });

        // Classify persona using the Reference API, with fallback to local
        // classification when the Reference API lacks a Technical Classification entry.
        if (cancelled) return;
        const papiJobDataLocal = { jobFamilyName, jobProfileName, jobLevel };
        const referenceClassification = await classifyPersonaAsync(jobProfileId);
        if (cancelled) return;

        // If the Reference API returned NON_TECH but the local job data
        // (jobFamilyName / jobProfileName) indicates TECH, trust the local data.
        // This handles cases where the Reference API doesn't have a
        // "Technical Classification" group for certain job profiles.
        const localClassification = classifyPersona(papiJobDataLocal);
        const classification =
          referenceClassification === PERSONA.NON_TECH && localClassification === PERSONA.TECH
            ? PERSONA.TECH
            : referenceClassification;
        setPersonaClassification(classification);

        // Emit telemetry once per alias per session
        emitPersonaTelemetry(papiAlias, classification, true);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch persona:', {
          alias: papiAlias,
          error: error.message,
          name: error.name,
          api: 'getEmployeeDetailsByLogin',
        });
        setHasApiName(false);
        setUserName(firstNameRef.current);
        setPersonaText('For persona based conversations, change the source in the settings.');
        setPersonaClassification(DEFAULT_PERSONA_CLASSIFICATION);
        setPapiJobData(null);

        // Emit telemetry once per alias per session (failure path)
        emitPersonaTelemetry(papiAlias, DEFAULT_PERSONA_CLASSIFICATION, false);
      }
    };
    fetchPersona();
    return () => { cancelled = true; };
  }, [papiAlias, enabled]);

  return {
    personaText,
    userName,
    personaClassification,
    effectivePersona,
    overrideMode,
    setOverrideMode,
    papiJobData,
  };
};
